import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = [{
  title: 'Información que Recopilamos',
  text: 'Recopilamos únicamente la información necesaria para procesar tu compra y entregarte el producto digital, como tu nombre y correo electrónico. No almacenamos datos de pago; estos son procesados de forma segura por nuestro proveedor de pagos.'
}, {
  title: 'Uso de la Información',
  text: 'Tu información personal se utiliza exclusivamente para: entregar el producto digital adquirido, enviar confirmaciones de compra y comunicaciones relacionadas con tu pedido. No vendemos, alquilamos ni compartimos tu información personal con terceros.'
}, {
  title: 'Protección de Datos',
  text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
}, {
  title: 'Cookies',
  text: 'Nuestro sitio puede utilizar cookies esenciales para el funcionamiento básico del sitio web. No utilizamos cookies de seguimiento publicitario.'
}, {
  title: 'Tus Derechos',
  text: 'Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes contactarnos a través de los canales disponibles en nuestro sitio.'
}, {
  title: 'Cambios en esta Política',
  text: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de última actualización.'
}];
const termsContent = [{
  title: 'Naturaleza del Producto',
  text: 'Biblia Anti-Ansiedad es un producto digital (PDF) de carácter espiritual y educativo. No constituye asesoramiento médico, psicológico ni psiquiátrico profesional. Si experimentas una crisis de salud mental, busca ayuda profesional inmediata.'
}, {
  title: 'Entrega del Producto',
  text: 'Al completar tu compra, recibirás acceso inmediato al producto digital. La entrega es instantánea y electrónica; no se envía ningún producto físico.'
}, {
  title: 'Política de Reembolso',
  text: 'Debido a la naturaleza digital e instantánea del producto, todas las ventas son finales. Al realizar la compra, aceptas que estás adquiriendo un producto digital de acceso inmediato.'
}, {
  title: 'Propiedad Intelectual',
  text: 'Todo el contenido de Biblia Anti-Ansiedad, incluyendo textos, diseños y metodologías, está protegido por derechos de autor. Queda prohibida su reproducción, distribución o reventa sin autorización expresa.'
}, {
  title: 'Uso Personal',
  text: 'El producto adquirido es para uso personal y exclusivo del comprador. No está permitido compartir, distribuir o revender el contenido a terceros.'
}, {
  title: 'Limitación de Responsabilidad',
  text: 'No nos hacemos responsables de los resultados individuales obtenidos con el uso de esta guía. Los resultados pueden variar según cada persona. Este producto complementa, pero no sustituye, el tratamiento profesional de salud mental.'
}];
function Modal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const isPrivacy = type === 'privacy';
  const title = isPrivacy ? 'Política de Privacidad' : 'Términos y Condiciones';
  const content = isPrivacy ? privacyContent : termsContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.25
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-26">
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} transition={{
      duration: 0.3,
      ease: [0.21, 0.47, 0.32, 0.98]
    }} className="bg-navy-light border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-27">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10" data-id="element-28">
          <h3 className="text-xl font-serif font-bold text-gold" data-id="element-29">{title}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors" aria-label="Cerrar" data-id="element-30">
            <XIcon className="w-5 h-5 text-cream-muted" data-id="element-31" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-80px)] space-y-6" data-id="element-32">
          <p className="text-xs text-cream-muted/50" data-id="element-33">
            Última actualización: Abril 2026
          </p>
          {content.map((section, idx) => <div key={idx} data-id="element-34">
              <h4 className="text-base font-semibold text-cream mb-2" data-id="element-35">
                {section.title}
              </h4>
              <p className="text-sm text-cream-muted/80 leading-relaxed" data-id="element-36">
                {section.text}
              </p>
            </div>)}
        </div>
      </motion.div>
    </motion.div>;
}
export const Footer = () => {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-navy-dark py-12 px-4 sm:px-6 lg:px-8 border-t border-white/5" data-id="element-37">
        <div className="max-w-4xl mx-auto text-center" data-id="element-38">
          <p className="text-cream-muted/60 text-sm leading-relaxed mb-6" data-id="element-39">
            Descargo de responsabilidad: Este producto es una guía espiritual y
            educativa, no sustituye el tratamiento médico, psicológico o
            psiquiátrico profesional. Si estás experimentando una crisis severa,
            por favor busca ayuda profesional inmediata.
          </p>

          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-40">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-cream-muted/50 hover:text-gold transition-colors duration-200 underline underline-offset-4 decoration-cream-muted/20 hover:decoration-gold/50" data-id="element-41">
              Política de Privacidad
            </button>
            <span className="text-cream-muted/20" data-id="element-42">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-cream-muted/50 hover:text-gold transition-colors duration-200 underline underline-offset-4 decoration-cream-muted/20 hover:decoration-gold/50" data-id="element-43">
              Términos y Condiciones
            </button>
          </div>

          <div className="w-12 h-px bg-white/10 mx-auto mb-6" data-id="element-44"></div>
          <p className="text-cream-muted/40 text-sm" data-id="element-45">
            © {new Date().getFullYear()} Biblia Anti-Ansiedad. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-46">
        {activeModal && <Modal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-47" />}
      </AnimatePresence>
    </>;
};